import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { ArrowDown } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1767725136371-7cfd3d88f35a?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

const lineVariants = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.35 + i * 0.13, ease: [0.16, 1, 0.3, 1] },
  }),
};

function Counter() {
  const [val, setVal] = useState(1);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const dur = 1800;
    const tick = (t) => {
      const p = Math.min((t - start) / dur, 1);
      setVal(Math.round(1 + p * 99));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    const timer = setTimeout(() => (raf = requestAnimationFrame(tick)), 900);
    return () => {
      clearTimeout(timer);
      cancelAnimationFrame(raf);
    };
  }, []);
  return (
    <span className="font-mono-num tabular-nums">
      {String(val).padStart(3, "0")}
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-20%"]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero"
      className="relative min-h-screen w-full overflow-hidden bg-[#0d2c54]"
    >
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute inset-0"
      >
        <img
          src={HERO_IMG}
          alt="Verona"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0d2c54]/75" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0d2c54] via-transparent to-[#0d2c54]/40" />
      </motion.div>

      <motion.div
        style={{ y: textY }}
        className="relative z-10 mx-auto max-w-[1500px] px-5 md:px-10 min-h-screen flex flex-col justify-end pb-16 md:pb-24 pt-28"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-2 w-2 bg-[#d9381e]" />
          <span className="font-mono-num uppercase text-xs md:text-sm tracking-[0.25em] text-white/80">
            Elezioni Comunali di Verona · Programma
          </span>
        </motion.div>

        <h1 className="font-display font-black uppercase text-white leading-[0.82] tracking-tighter text-[18vw] md:text-[13vw] lg:text-[11rem]">
          {["100 Idee", "per", "Verona"].map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="show"
                className={`block ${i === 2 ? "text-[#d9381e]" : ""}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.8 }}
            className="max-w-md text-white/85 text-base md:text-lg leading-relaxed font-medium"
          >
            Non promesse, ma proposte concrete. Cento idee per una città più
            forte, sicura e vicina alle persone. Un impegno, punto per punto.
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.0, duration: 0.8 }}
            className="flex items-end gap-3 text-white"
          >
            <span className="font-display font-black text-6xl md:text-8xl leading-none tracking-tighter">
              <Counter />
            </span>
            <span className="font-mono-num text-xl md:text-2xl text-white/50 pb-1 md:pb-2">
              / 100
            </span>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.6 }}
        className="absolute bottom-6 right-5 md:right-10 z-10 flex items-center gap-2 text-white/60"
      >
        <span className="font-mono-num uppercase text-[10px] tracking-widest">
          Scorri
        </span>
        <ArrowDown className="h-4 w-4 animate-bounce" />
      </motion.div>
    </section>
  );
}
